import { useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { SelectField } from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import { ApiError } from '@/lib/api';
import { use_pet_medical } from '@/features/pet/hooks';
import {
  ALLERGY_SEVERITY_OPTIONS,
  CONDITION_STATUS_OPTIONS,
  VACCINATION_TYPE_OPTIONS,
  type AllergySeverity,
  type ConditionStatus,
  type VaccinationType,
} from '@/features/pet/types';

const today = () => new Date().toISOString().slice(0, 10);

function Block({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <Label>{title}</Label>
      {children}
    </div>
  );
}

export function PetMedicalSection({ pet_id }: { pet_id: string }) {
  const med = use_pet_medical(pet_id);
  const [err, set_err] = useState<string | null>(null);

  // Local add-form state per block.
  const [vax, set_vax] = useState({
    type: 'RABIES' as VaccinationType,
    administered_on: today(),
    next_due_on: '',
    administered_by: '',
  });
  const [allergy, set_allergy] = useState({ allergen: '', severity: 'MILD' as AllergySeverity });
  const [cond, set_cond] = useState({
    condition: '',
    status: 'ACTIVE' as ConditionStatus,
    diagnosed_on: '',
  });

  function guard<T>(p: Promise<T>) {
    set_err(null);
    return p.catch((e) => set_err(e instanceof ApiError ? e.message : 'Something went wrong'));
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="font-heading text-base font-bold">Medical records</h3>
        <p className="text-xs text-muted-foreground">
          Vaccinations, allergies, and conditions a service provider should know about.
        </p>
      </div>
      {err && (
        <p className="text-xs text-destructive" data-testid="medical-error">
          {err}
        </p>
      )}

      {/* ---- Vaccinations ---- */}
      <Block title="Vaccinations">
        <ul className="flex flex-col divide-y-2 divide-brand-outline-variant/10" data-testid="vax-list">
          {(med.vaccinations.data ?? []).map((v) => (
            <li key={v.id} data-testid={`vax-row-${v.id}`} className="flex items-center gap-2 py-2 text-sm">
              <span className="font-semibold">
                {VACCINATION_TYPE_OPTIONS.find((o) => o.value === v.type)?.label ?? v.type}
              </span>
              <span className="text-muted-foreground">given {v.administered_on}</span>
              {v.next_due_on && (
                <span className="text-muted-foreground">· next due {v.next_due_on}</span>
              )}
              <button
                type="button"
                className="ml-auto text-muted-foreground hover:text-destructive"
                onClick={() => guard(med.remove_vaccination.mutateAsync(v.id))}
                aria-label="Delete vaccination"
                data-testid={`vax-delete-${v.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
          {med.vaccinations.data?.length === 0 && (
            <li className="py-2 text-xs text-muted-foreground" data-testid="vax-empty">
              No vaccinations recorded.
            </li>
          )}
        </ul>
        <div className="flex flex-wrap items-end gap-2">
          <SelectField
            className="w-44"
            value={vax.type}
            onValueChange={(v) => set_vax((s) => ({ ...s, type: v as VaccinationType }))}
            data-testid="vax-add-type"
            aria-label="Vaccination type"
            options={VACCINATION_TYPE_OPTIONS}
          />
          <DatePicker
            className="w-44"
            value={vax.administered_on}
            max={today()}
            onChange={(v) => set_vax((s) => ({ ...s, administered_on: v }))}
            data-testid="vax-add-date"
            aria-label="Administered on"
            placeholder="Administered on"
          />
          <DatePicker
            className="w-44"
            value={vax.next_due_on}
            onChange={(v) => set_vax((s) => ({ ...s, next_due_on: v }))}
            data-testid="vax-add-next-due"
            aria-label="Next due on"
            placeholder="Next due"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={med.add_vaccination.isPending || !vax.administered_on}
            data-testid="vax-add-submit"
            onClick={async () => {
              await guard(
                med.add_vaccination.mutateAsync({
                  type: vax.type,
                  administered_on: vax.administered_on,
                  next_due_on: vax.next_due_on || undefined,
                  administered_by: vax.administered_by || undefined,
                }),
              );
              set_vax((s) => ({ ...s, next_due_on: '', administered_by: '' }));
            }}
          >
            {med.add_vaccination.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Add
          </Button>
        </div>
      </Block>

      {/* ---- Allergies ---- */}
      <Block title="Allergies">
        <ul className="flex flex-col divide-y-2 divide-brand-outline-variant/10" data-testid="allergy-list">
          {(med.allergies.data ?? []).map((a) => (
            <li key={a.id} data-testid={`allergy-row-${a.id}`} className="flex items-center gap-2 py-2 text-sm">
              <span className="font-semibold">{a.allergen}</span>
              <span className="text-muted-foreground">
                · {ALLERGY_SEVERITY_OPTIONS.find((o) => o.value === a.severity)?.label}
              </span>
              <button
                type="button"
                className="ml-auto text-muted-foreground hover:text-destructive"
                onClick={() => guard(med.remove_allergy.mutateAsync(a.id))}
                aria-label="Delete allergy"
                data-testid={`allergy-delete-${a.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
          {med.allergies.data?.length === 0 && (
            <li className="py-2 text-xs text-muted-foreground" data-testid="allergy-empty">
              No allergies recorded.
            </li>
          )}
        </ul>
        <div className="flex flex-wrap items-end gap-2">
          <Input
            className="w-auto flex-1"
            placeholder="Allergen (e.g. Chicken)"
            value={allergy.allergen}
            onChange={(e) => set_allergy((s) => ({ ...s, allergen: e.target.value }))}
            data-testid="allergy-add-allergen"
            aria-label="Allergen"
          />
          <SelectField
            className="w-44"
            value={allergy.severity}
            onValueChange={(v) => set_allergy((s) => ({ ...s, severity: v as AllergySeverity }))}
            data-testid="allergy-add-severity"
            aria-label="Severity"
            options={ALLERGY_SEVERITY_OPTIONS}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={med.add_allergy.isPending || allergy.allergen.trim() === ''}
            data-testid="allergy-add-submit"
            onClick={async () => {
              await guard(
                med.add_allergy.mutateAsync({ allergen: allergy.allergen.trim(), severity: allergy.severity }),
              );
              set_allergy({ allergen: '', severity: 'MILD' });
            }}
          >
            {med.add_allergy.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Add
          </Button>
        </div>
      </Block>

      {/* ---- Conditions ---- */}
      <Block title="Conditions">
        <ul className="flex flex-col divide-y-2 divide-brand-outline-variant/10" data-testid="condition-list">
          {(med.conditions.data ?? []).map((c) => (
            <li key={c.id} data-testid={`condition-row-${c.id}`} className="flex items-center gap-2 py-2 text-sm">
              <span className="font-semibold">{c.condition}</span>
              <span className="text-muted-foreground">
                · {CONDITION_STATUS_OPTIONS.find((o) => o.value === c.status)?.label}
              </span>
              {c.diagnosed_on && <span className="text-muted-foreground">· dx {c.diagnosed_on}</span>}
              <button
                type="button"
                className="ml-auto text-muted-foreground hover:text-destructive"
                onClick={() => guard(med.remove_condition.mutateAsync(c.id))}
                aria-label="Delete condition"
                data-testid={`condition-delete-${c.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
          {med.conditions.data?.length === 0 && (
            <li className="py-2 text-xs text-muted-foreground" data-testid="condition-empty">
              No conditions recorded.
            </li>
          )}
        </ul>
        <div className="flex flex-wrap items-end gap-2">
          <Input
            className="w-auto flex-1"
            placeholder="Condition (e.g. Hip dysplasia)"
            value={cond.condition}
            onChange={(e) => set_cond((s) => ({ ...s, condition: e.target.value }))}
            data-testid="condition-add-name"
            aria-label="Condition"
          />
          <SelectField
            className="w-44"
            value={cond.status}
            onValueChange={(v) => set_cond((s) => ({ ...s, status: v as ConditionStatus }))}
            data-testid="condition-add-status"
            aria-label="Status"
            options={CONDITION_STATUS_OPTIONS}
          />
          <DatePicker
            className="w-44"
            value={cond.diagnosed_on}
            max={today()}
            onChange={(v) => set_cond((s) => ({ ...s, diagnosed_on: v }))}
            data-testid="condition-add-date"
            aria-label="Diagnosed on"
            placeholder="Diagnosed on"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={med.add_condition.isPending || cond.condition.trim() === ''}
            data-testid="condition-add-submit"
            onClick={async () => {
              await guard(
                med.add_condition.mutateAsync({
                  condition: cond.condition.trim(),
                  status: cond.status,
                  diagnosed_on: cond.diagnosed_on || undefined,
                }),
              );
              set_cond({ condition: '', status: 'ACTIVE', diagnosed_on: '' });
            }}
          >
            {med.add_condition.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Add
          </Button>
        </div>
      </Block>
    </div>
  );
}
